import {
  computed,
  ComputedRef,
  nextTick,
  onUnmounted,
  ref,
  Ref,
  watch,
  unref,
  MaybeRef,
} from 'vue';
import { useCleanup } from '@/composables/useCleanup';
import {
  NumberOnlyAggregations,
  PreparedItem,
  PreparedRelationship,
  PreparedWorkItem,
} from '@/models/item.models';
import { useRoot } from './component';
import router, { RouteNames } from '@/router';
import { organizationData } from '@/store/helpers';
import uniq from 'lodash-es/uniq';
import { GleapFlows } from '@/models/common.models';
import { LocalEvents } from '@/store/events';
import { estimateString } from './estimate';
import { isNil } from 'lodash-es';
import { configure } from 'safe-stable-stringify';
import { useStorage } from '@vueuse/core';
import { LocalStorageKeys } from './local-storage';
import { timeout } from './time';
import {
  fetchItemListByNumbers,
  isImportedItem,
  isIntegrationLinkedFolder,
} from './items';
import {
  Colors,
  Folder,
  ItemType,
  RelationshipType,
  WorkItem,
  WorkType,
} from '@/api';
import dayjs from '@/utils/dayjs';
import { useIntl } from '@/utils/internationalization';

export function toggleGlobalLoader(value: boolean) {
  const globalLoader = document.getElementById('global-loader');
  if (!globalLoader) return;
  if (value) {
    globalLoader.classList.remove('fade-out');
    globalLoader.classList.add('show');
  } else {
    // Add fade-out animation class
    globalLoader.classList.add('fade-out');
    // Remove show class after animation completes
    setTimeout(() => {
      globalLoader.classList.remove('show');
      globalLoader.classList.remove('fade-out');
    }, 800); // Match animation duration
  }
}

export const useOverflowTooltip = (
  refName: string
): {
  [key: string]: Ref<unknown> | (() => void);
} => {
  const { onCleanup } = useCleanup();
  const element = ref<HTMLElement | HTMLElement[] | null>(null);
  const hasTooltipMap = ref<boolean[]>([]);

  const listeners: { el: WeakRef<HTMLElement>; listener: () => void }[] = [];

  const clearAllListeners = () => {
    listeners.forEach(({ el: elRef, listener }) => {
      const el = elRef.deref();
      if (!el) return;
      el.removeEventListener('mouseenter', listener);
    });
    while (listeners.length) listeners.pop();
  };

  const setTooltipListeners = () => {
    const elements = Array.isArray(element.value)
      ? element.value
      : [element.value];

    // clear existing event listeners
    clearAllListeners();

    // add fresh listeners
    elements.forEach((el, idx) => {
      hasTooltipMap.value[idx] = false;
      if (el) {
        const listener = () => {
          hasTooltipMap.value[idx] = el.scrollWidth > el.clientWidth;
        };
        el.addEventListener('mouseenter', listener);
        listeners.push({ el: new WeakRef(el as HTMLElement), listener });
      }
    });
  };

  const stopWatcher = watch(
    () => element.value,
    async () => {
      await nextTick();
      setTooltipListeners();
    },
    { immediate: true, deep: true }
  );

  onCleanup(() => {
    stopWatcher();
    clearAllListeners();
  });

  return {
    [`${refName}`]: element,
    [`${refName}HasTooltipMap`]: hasTooltipMap,
    [`${refName}SetTooltips`]: setTooltipListeners,
  };
};

export function useNavigateToItem(root: ReturnType<typeof useRoot>) {
  const { organization } = organizationData();
  return {
    navigateToItem: (
      item: PreparedItem | WorkItem | Folder,
      blank?: boolean
    ) => {
      const route = {
        name: root.$route.name || RouteNames.Map,
        params: root.$route.params,
        query: {
          ...root.$route.query,
          itemNumber: item.number,
        },
      };
      if (blank) {
        window.open(root.$router.resolve(route).href, '_blank');
      } else {
        root.$router.push(route);
      }
    },
    navigateToItemInMap: (item: PreparedItem | WorkItem | Folder) => {
      root.$router.push({
        name: RouteNames.Map,
        params: {
          organization: organization.value?.name,
        },
        query: {
          selectedFolder: item.number,
          tab: 'portfolio',
        },
      });
    },
  };
}

export function getMimeTypeFromDataUrl(dataUrl: string) {
  if (!isValidDataUrl(dataUrl)) return;
  const data = dataUrl.split(',')[0];
  const mimeType = data.split(':')[1].split(';')[0];
  return mimeType;
}

export function isValidDataUrl(dataUrl: string) {
  const regex =
    /^data:([a-z]+\/[a-z0-9-+.]+(;[a-z0-9-.!#$%*+.{}|~`]+=[a-z0-9-.!#$%*+.{}()|~`]+)*)?(;base64)?,([a-z0-9!$&',()*+;=\-._~:@/?%\s<>]*?)$/i;
  return regex.test((dataUrl || '').trim());
}

export async function srcToFile(src: string, fileName: string) {
  const mimeType = getMimeTypeFromDataUrl(src);
  if (!mimeType) {
    src = await urlToDataUrl(src);
  }
  const res = await fetch(src);
  const buf = await res.arrayBuffer();
  return new File([buf], fileName, { type: getMimeTypeFromDataUrl(src) });
}

export async function urlToDataUrl(url: string) {
  const res = await fetch(url);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

export function getItemLink(itemNumber: string) {
  const { organization } = organizationData();
  const routeInfo = router.resolve({
    name: RouteNames.Item,
    params: { itemNumber, organization: organization.value?.name },
  });
  return new URL(routeInfo.href, window.location.href).href;
}

export function workTypesHaveDifferentCollections(
  workTypeIds: Array<WorkType['id']>
): boolean {
  const { workTypes } = organizationData();
  const workType = workTypes.value.filter((it) => workTypeIds.includes(it.id));

  const priorityCollectionIds = uniq(
    workType.map((type) => type.priorityCollectionId)
  );
  if (priorityCollectionIds.length > 1) return true;

  const customFieldCollectionIds = uniq(
    workType.map((type) => type.customFieldCollectionId)
  );
  if (customFieldCollectionIds.length > 1) return true;

  const labelCollectionIds = uniq(
    workType.map((type) => type.labelCollectionId)
  );
  if (labelCollectionIds.length > 1) return true;

  return false;
}

export function useDeleteItems(root: ReturnType<typeof useRoot>) {
  const itemDeletingMap = ref(new Map<string, boolean>());
  const multiDeleteLoading = ref(false);

  const handleValidationWarnings = (
    items: Array<PreparedItem>
  ): {
    shouldAbort: boolean;
  } => {
    if (items.some(isImportedItem)) {
      root.$notify({
        type: 'warning',
        message: 'Imported items cannot be deleted.',
      });
      return { shouldAbort: true };
    }

    if (items.some(isIntegrationLinkedFolder)) {
      root.$notify({
        type: 'warning',
        message: 'Integration linked workstreams cannot be deleted.',
      });
      return { shouldAbort: true };
    }

    return { shouldAbort: false };
  };

  function deleteItem(item: PreparedItem) {
    let message = `Are you sure you want to delete "${item.title}"?`;
    if (item.itemType === ItemType.FOLDER) {
      message += ' All items inside this workstream will also be deleted.';
    } else if (item.canHaveChildren) {
      message += ' All child items inside this item will also be deleted.';
    }
    root.$dialog({
      title: 'Confirm Delete',
      color: Colors.RED,

      message,
      okLabel: 'Delete',
      onOk: async () => {
        itemDeletingMap.value.set(item.id, true);
        try {
          await root.$store.item.deleteWorkItem({
            itemNumber: item.number,
            itemType: item.itemType,
            itemId: item.id,
          });
          const noun =
            item.itemType === ItemType.FOLDER ? 'workstream' : 'item';
          root.$notify({
            type: 'success',
            message: `The ${noun} was deleted successfully.`,
          });
          LocalEvents.emit('item-deleted', item);
        } catch (e) {
          root.$notify({
            type: 'error',
            message:
              'There was an error deleting the item. If this problem persists, please contact support.',
          });
        } finally {
          itemDeletingMap.value.delete(item.id);
        }
      },
    });
  }

  function onDelete(items: Array<PreparedItem>) {
    const { shouldAbort } = handleValidationWarnings(items);
    if (shouldAbort) return;

    if (items.length === 1) {
      return deleteItem(items[0]);
    }
    let message = `Are you sure you want to delete the selected ${items.length} items?`;
    if (items.some((i) => i.itemType === ItemType.FOLDER)) {
      message += ' All items inside the workstreams will also be deleted.';
    } else if (items.some((i) => i.canHaveChildren)) {
      message += ' All child items inside the items will also be deleted.';
    }

    root.$dialog({
      title: 'Confirm Delete',
      color: Colors.RED,

      message,
      okLabel: 'Delete',
      onOk: async () => {
        multiDeleteLoading.value = true;
        const deletePromises: Array<Promise<void>> = [];
        items.forEach((item) => {
          deletePromises.push(
            root.$store.item.deleteWorkItem({
              itemNumber: item.number,
              itemType: item.itemType,
              itemId: item.id,
            })
          );
        });
        try {
          await Promise.all(deletePromises);
          root.$notify({
            type: 'success',
            message: `The items were deleted successfully.`,
          });
          for (const item of items) {
            LocalEvents.emit('item-deleted', item);
          }
        } catch (e) {
          root.$notify({
            type: 'error',
            message:
              'There was an error deleting the items. If this problem persists, please contact support.',
          });
        } finally {
          multiDeleteLoading.value = false;
        }
      },
    });
  }

  return {
    itemDeletingMap,
    multiDeleteLoading,
    onDelete,
  };
}

export function useArchiveItems(root: ReturnType<typeof useRoot>) {
  const itemArchivingMap = ref(new Map<string, boolean>());
  const multiArchiveLoading = ref(false);

  const handleValidationWarnings = (
    items: Array<PreparedItem>
  ): {
    shouldAbort: boolean;
  } => {
    if (items.some(isImportedItem)) {
      root.$notify({
        type: 'warning',
        message: 'Imported items cannot be archived.',
      });
      return { shouldAbort: true };
    }

    if (items.some(isIntegrationLinkedFolder)) {
      root.$notify({
        type: 'warning',
        message: 'Integration linked workstreams cannot be archived.',
      });
      return { shouldAbort: true };
    }

    return { shouldAbort: false };
  };

  function archiveItem(item: PreparedItem) {
    let message = `Are you sure you want to archive "${item.title}"?`;
    if (item.itemType === ItemType.FOLDER) {
      message += ' All items inside this workstream will also be archived.';
    }
    if (item.itemType === ItemType.WORKITEM && item.canHaveChildren) {
      message += ' All child items inside this item will also be archived.';
    }
    root.$dialog({
      title: 'Confirm Archive',
      color: Colors.YELLOW,

      message,
      okLabel: 'Archive',
      onOk: async () => {
        itemArchivingMap.value.set(item.id, true);
        try {
          await root.$store.item.archiveWorkItem({
            itemNumber: item.number,
            itemType: item.itemType,
            itemId: item.id,
          });
          const noun =
            item.itemType === ItemType.FOLDER ? 'workstream' : 'item';
          root.$notify({
            type: 'success',
            message: `The ${noun} was archived successfully.`,
          });
          LocalEvents.emit('item-archived', item);
        } catch (e) {
          root.$notify({
            type: 'error',
            message:
              'There was an error archiving the item. If this problem persists, please contact support.',
          });
        } finally {
          itemArchivingMap.value.delete(item.id);
        }
      },
    });
  }

  function onArchive(items: Array<PreparedItem>) {
    const { shouldAbort } = handleValidationWarnings(items);
    if (shouldAbort) return;

    const unarchivedItems = items.filter((i) => !i.archived);
    if (unarchivedItems.length === 1) {
      return archiveItem(unarchivedItems[0]);
    }
    let message = `Are you sure you want to archive the selected ${unarchivedItems.length} items?`;
    if (unarchivedItems.some((i) => i.itemType === ItemType.FOLDER)) {
      message += ' All items inside the workstreams will also be archived.';
    }
    if (
      unarchivedItems.some(
        (i) => i.itemType === ItemType.WORKITEM && i.canHaveChildren
      )
    ) {
      message += ' All child items inside the items will also be archived.';
    }

    root.$dialog({
      title: 'Confirm Archive',
      color: Colors.YELLOW,

      message,
      okLabel: 'Archive',
      onOk: async () => {
        multiArchiveLoading.value = true;
        const archivePromises: Array<Promise<void>> = [];
        unarchivedItems.forEach((item) => {
          archivePromises.push(
            root.$store.item.archiveWorkItem({
              itemNumber: item.number,
              itemType: item.itemType,
              itemId: item.id,
            })
          );
        });
        try {
          await Promise.all(archivePromises);
          root.$notify({
            type: 'success',
            message: `The items were archived successfully.`,
          });
          for (const item of unarchivedItems) {
            LocalEvents.emit('item-archived', item);
          }
        } catch (e) {
          root.$notify({
            type: 'error',
            message:
              'There was an error archiving the items. If this problem persists, please contact support.',
          });
        } finally {
          multiArchiveLoading.value = false;
        }
      },
    });
  }
  return {
    itemArchivingMap,
    multiArchiveLoading,
    onArchive,
  };
}

export function useUnarchiveItems(root: ReturnType<typeof useRoot>) {
  const doNotShowUnarchivedMessage = useStorage<boolean>(
    LocalStorageKeys.UNARCHIVE_ITEMS_MESSAGE_DO_NOT_SHOW,
    false
  );
  const unarchiveChildren = useStorage<boolean>(
    LocalStorageKeys.UNARCHIVE_ITEMS_CHILDREN_AS_WELL,
    false
  );
  const itemUnarchivingMap = ref(new Map<string, boolean>());
  const multiUnarchiveLoading = ref(false);

  async function unarchiveItemDialog(item: PreparedItem) {
    const message = `Are you sure you want to unarchive "${item.title}"? This will unarchive the parent as well.`;
    root.$dialog({
      title: 'Confirm Unarchive',
      color: Colors.YELLOW,

      message,
      okLabel: 'Unarchive',
      options: {
        type: 'checkbox',
        model: [],
        items: [
          {
            label: 'Unarchive child items as well',
            value: 'unarchiveChildren',
            color: 'primary',
          },
          {
            label: 'Do not show this dialog again',
            value: 'doNotShowDialogAgain',
            color: 'primary',
          },
        ],
      },
      onOk: async (data) => {
        if (data?.includes('doNotShowDialogAgain')) {
          doNotShowUnarchivedMessage.value = true;
        }
        unarchiveChildren.value = data?.includes('unarchiveChildren') ?? false;
        unarchiveItem(item, unarchiveChildren.value);
      },
    });
  }

  async function unarchiveItem(item: PreparedItem, unarchiveChildren: boolean) {
    itemUnarchivingMap.value.set(item.id, true);
    try {
      await root.$store.item.unarchiveItem({
        itemNumber: item.number,
        itemType: item.itemType,
        itemId: item.id,
        unarchiveChildren,
      });
      const noun = item.itemType === ItemType.FOLDER ? 'workstream' : 'item';
      root.$notify({
        type: 'success',
        message: `The ${noun} was unarchived successfully. ${
          unarchiveChildren ? 'Child items were unarchived as well.' : ''
        }`,
      });
      LocalEvents.emit('item-unarchived', { item, unarchiveChildren });
    } catch (e) {
      root.$notify({
        type: 'error',
        message:
          'There was an error unarchiving the item. If this problem persists, please contact support.',
      });
    } finally {
      itemUnarchivingMap.value.delete(item.id);
    }
  }

  function onUnarchive(items: Array<PreparedItem>) {
    const archivedItems = items.filter((i) => i.archived);
    const showUnarchiveDialog =
      archivedItems.length === 1 && !doNotShowUnarchivedMessage.value;
    if (!showUnarchiveDialog) {
      return unarchiveItem(archivedItems[0], unarchiveChildren.value);
    } else if (showUnarchiveDialog) {
      return unarchiveItemDialog(archivedItems[0]);
    } else if (archivedItems.length > 1 && doNotShowUnarchivedMessage.value) {
      return onUnarchiveMultiple(archivedItems);
    }
    const message = `Are you sure you want to unarchive the selected ${archivedItems.length} items?`;

    root.$dialog({
      title: 'Confirm Unarchive',
      color: Colors.YELLOW,

      message,
      okLabel: 'Unarchive',
      options: {
        type: 'checkbox',
        model: [],
        items: [
          {
            label: 'Unarchive child items as well',
            value: 'unarchiveChildren',
            color: 'primary',
          },
          {
            label: 'Do not show this dialog again',
            value: 'doNotShowDialogAgain',
            color: 'primary',
          },
        ],
      },
      onOk: async (data) => {
        if (data?.includes('doNotShowDialogAgain')) {
          doNotShowUnarchivedMessage.value = true;
        }
        unarchiveChildren.value = data?.includes('unarchiveChildren') ?? false;
        onUnarchiveMultiple(archivedItems, unarchiveChildren.value);
      },
    });
  }

  async function onUnarchiveMultiple(
    items: Array<PreparedItem>,
    unarchiveChildren = false
  ) {
    multiUnarchiveLoading.value = true;
    const unarchivePromises: Array<Promise<void>> = [];
    items.forEach((item) => {
      unarchivePromises.push(
        root.$store.item.unarchiveItem({
          itemNumber: item.number,
          itemType: item.itemType,
          itemId: item.id,
          unarchiveChildren,
        })
      );
    });
    try {
      await Promise.all(unarchivePromises);
      root.$notify({
        type: 'success',
        message: `The items were unarchived successfully.`,
      });
      for (const item of items) {
        LocalEvents.emit('item-unarchived', { item, unarchiveChildren });
      }
    } catch (e) {
      root.$notify({
        type: 'error',
        message:
          'There was an error unarchiving the items. If this problem persists, please contact support.',
      });
    } finally {
      multiUnarchiveLoading.value = false;
    }
  }
  return {
    itemUnarchivingMap,
    multiUnarchiveLoading,
    onUnarchive,
  };
}

export function mergeAggregations(
  baseAggregations: NumberOnlyAggregations,
  archivedAggregations: NumberOnlyAggregations
) {
  const mergedAggregations = {
    totalItemCount:
      baseAggregations.totalItemCount + archivedAggregations.totalItemCount,
    totalCompletedItemCount:
      baseAggregations.totalCompletedItemCount +
      archivedAggregations.totalCompletedItemCount,
    totalEstimate:
      baseAggregations.totalEstimate + archivedAggregations.totalEstimate,
    totalEstimateCompleted:
      baseAggregations.totalEstimateCompleted +
      archivedAggregations.totalEstimateCompleted,
    totalEstimateSd:
      baseAggregations.totalEstimateSd + archivedAggregations.totalEstimateSd,
    totalEstimateSdCompleted:
      baseAggregations.totalEstimateSdCompleted +
      archivedAggregations.totalEstimateSdCompleted,
    totalPointEffort:
      baseAggregations.totalPointEffort + archivedAggregations.totalPointEffort,
    totalPointEffortCompleted:
      baseAggregations.totalPointEffortCompleted +
      archivedAggregations.totalPointEffortCompleted,
  };
  return mergedAggregations;
}

/**
 * Non-reactive version of useItemAggregations for use in loops.
 * IMPORTANT: Use this instead of useItemAggregations when computing stats for multiple items
 * in a loop to avoid creating Vue computed refs that accumulate and cause memory leaks.
 *
 * @param item The item to compute aggregations for
 * @param orgIsUsingPointEstimates Whether the org uses point estimates (pass from component-level organizationData)
 * @param orgIsUsingTimeEstimates Whether the org uses time estimates (pass from component-level organizationData)
 * @param includeArchived Whether to include archived items in aggregations
 */
export function getItemAggregationStats(
  item: PreparedItem,
  orgIsUsingPointEstimates: boolean,
  orgIsUsingTimeEstimates: boolean,
  includeArchived = false
) {
  const aggregations = includeArchived
    ? mergeAggregations(
        item.baseItemAggregations,
        item.baseArchivedItemAggregations
      )
    : item.baseItemAggregations;

  return {
    orgIsUsingPointEstimates,
    orgIsUsingTimeEstimates,
    isUsingManualProgress: !isNil(item.manualPercentCompleted),
    manualPercentCompleted: !isNil(item.manualPercentCompleted)
      ? item.manualPercentCompleted
      : undefined,
    itemsCompleted:
      aggregations.totalCompletedItemCount.toLocaleString('en-US'),
    itemsTotal: aggregations.totalItemCount.toLocaleString('en-US'),
    timeEstimateCompleted: orgIsUsingTimeEstimates
      ? Math.round(aggregations.totalEstimateCompleted).toLocaleString(
          'en-US'
        ) || 0
      : undefined,
    timeEstimateSdCompleted: orgIsUsingTimeEstimates
      ? estimateString(Math.round(aggregations.totalEstimateSdCompleted), {
          showUnitOfTime: false,
        })
      : undefined,
    timeEstimateTotal: orgIsUsingTimeEstimates
      ? Math.round(aggregations.totalEstimate).toLocaleString('en-US') || 0
      : undefined,
    timeEstimateSdTotal: orgIsUsingTimeEstimates
      ? estimateString(Math.round(aggregations.totalEstimateSd), {
          showUnitOfTime: false,
        })
      : undefined,
    pointEstimateCompleted: orgIsUsingPointEstimates
      ? Math.round(aggregations.totalPointEffortCompleted).toLocaleString(
          'en-US'
        ) || 0
      : undefined,
    pointEstimateTotal: orgIsUsingPointEstimates
      ? Math.round(aggregations.totalPointEffort).toLocaleString('en-US') || 0
      : undefined,
    itemCompletedPercentage: aggregations.totalItemCount
      ? Math.round(
          (aggregations.totalCompletedItemCount / aggregations.totalItemCount) *
            100
        )
      : 0,
    timeEstimateCompletedPercentage: orgIsUsingTimeEstimates
      ? aggregations.totalEstimate
        ? Math.round(
            (aggregations.totalEstimateCompleted / aggregations.totalEstimate) *
              100
          )
        : 0
      : undefined,
    pointEstimateCompletedPercentage: orgIsUsingPointEstimates
      ? aggregations.totalPointEffort
        ? Math.round(
            (aggregations.totalPointEffortCompleted /
              aggregations.totalPointEffort) *
              100
          )
        : 0
      : undefined,
  };
}

export function useItemAggregations({
  itemMaybeRef,
  includeArchivedRef = false,
}: {
  itemMaybeRef:
    | ComputedRef<PreparedItem | undefined>
    | PreparedItem
    | undefined;
  includeArchivedRef?: ComputedRef<boolean> | boolean;
}) {
  // IMPORTANT: Call organizationData() OUTSIDE the computed to avoid creating
  // 29 computed refs every time the computed is evaluated. This was causing a
  // massive memory leak when useItemAggregations was called in a loop.
  const {
    orgHasWorkTypesUsingPointEstimates,
    orgHasWorkTypesUsingTimeEstimates,
  } = organizationData();

  const aggregatedCompletionStats = computed(() => {
    const item = unref(itemMaybeRef);
    const includeArchived = unref(includeArchivedRef);
    if (!item) return;
    const orgIsUsingPointEstimates = orgHasWorkTypesUsingPointEstimates.value;
    const orgIsUsingTimeEstimates = orgHasWorkTypesUsingTimeEstimates.value;

    const aggregations = includeArchived
      ? mergeAggregations(
          item.baseItemAggregations,
          item.baseArchivedItemAggregations
        )
      : item.baseItemAggregations;
    return {
      orgIsUsingPointEstimates,
      orgIsUsingTimeEstimates,
      isUsingManualProgress: !isNil(item.manualPercentCompleted),
      manualPercentCompleted: !isNil(item.manualPercentCompleted)
        ? item.manualPercentCompleted
        : undefined,
      itemsCompleted:
        aggregations.totalCompletedItemCount.toLocaleString('en-US'),
      itemsTotal: aggregations.totalItemCount.toLocaleString('en-US'),
      timeEstimateCompleted: orgIsUsingTimeEstimates
        ? Math.round(aggregations.totalEstimateCompleted).toLocaleString(
            'en-US'
          ) || 0
        : undefined,
      timeEstimateSdCompleted: orgIsUsingTimeEstimates
        ? estimateString(Math.round(aggregations.totalEstimateSdCompleted), {
            showUnitOfTime: false,
          })
        : undefined,
      timeEstimateTotal: orgIsUsingTimeEstimates
        ? Math.round(aggregations.totalEstimate).toLocaleString('en-US') || 0
        : undefined,
      timeEstimateSdTotal: orgIsUsingTimeEstimates
        ? estimateString(Math.round(aggregations.totalEstimateSd), {
            showUnitOfTime: false,
          })
        : undefined,
      pointEstimateCompleted: orgIsUsingPointEstimates
        ? Math.round(aggregations.totalPointEffortCompleted).toLocaleString(
            'en-US'
          ) || 0
        : undefined,
      pointEstimateTotal: orgIsUsingPointEstimates
        ? Math.round(aggregations.totalPointEffort).toLocaleString('en-US') || 0
        : undefined,
      itemCompletedPercentage:
        Math.round(
          (aggregations.totalCompletedItemCount / aggregations.totalItemCount) *
            100
        ) || 0,
      timeEstimateCompletedPercentage: orgIsUsingTimeEstimates
        ? Math.round(
            (aggregations.totalEstimateCompleted / aggregations.totalEstimate) *
              100
          ) || 0
        : undefined,
      pointEstimateCompletedPercentage: orgIsUsingPointEstimates
        ? Math.round(
            (aggregations.totalPointEffortCompleted /
              aggregations.totalPointEffort) *
              100
          ) || 0
        : undefined,
    };
  });

  return {
    aggregatedCompletionStats,
  };
}

export function usePlatform() {
  const platform =
    (navigator as Navigator & { userAgentData?: { platform: string } })
      ?.userAgentData?.platform ||
    navigator?.platform ||
    'unknown';
  const isMac = platform.toUpperCase().indexOf('MAC') >= 0;

  const metaChar = isMac ? '⌘' : 'Ctrl';

  return {
    isMac,
    platform,
    metaChar,
  };
}

export function randomUniqueIntegers(total: number, quantity: number) {
  const numbers = Array(total)
    .fill(null)
    .map((_, i) => i + 1);

  return numbers
    .map((value) => ({ value, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .map(({ value }) => value)
    .slice(0, quantity);
}

export function randomOrderedUniqueIntegers(total: number, quantity: number) {
  return randomUniqueIntegers(total, quantity).sort((a, b) => a - b);
}

export function useLRUArray<T>({
  max,
  isEqual,
  localStorageKey,
}: {
  max: number;
  isEqual?: (a: T, b: T) => boolean;
  localStorageKey?: MaybeRef<string>;
}) {
  const _array: Ref<T[]> = ref([]);
  const lsKey = computed(() => unref(localStorageKey));
  if (lsKey.value) {
    const savedArray = safeParseJSON(localStorage.getItem(lsKey.value) || '[]');
    if (Array.isArray(savedArray)) {
      _array.value = savedArray;
    }
  }
  const saveToLocalStorage = () => {
    if (lsKey.value) {
      localStorage.setItem(lsKey.value, JSON.stringify(_array.value));
    }
  };

  /** natural order for passing to `push` */
  const arrayRecentsLast = computed(() => [..._array.value]);

  /** natural order for `v-for` */
  const arrayRecentsFirst = computed(() => [..._array.value].reverse());

  /** last item is the most recent (arrayRecentLast) */
  function push(...items: Array<T>) {
    items.forEach((item) => {
      const existingIndex = _array.value.findIndex((existingItem) =>
        isEqual ? isEqual(existingItem, item) : existingItem === item
      );
      if (existingIndex !== -1) {
        _array.value.splice(existingIndex, 1);
        _array.value.push(item);
      } else {
        _array.value.push(item);
        if (_array.value.length > max) {
          _array.value.shift();
        }
      }
    });
    saveToLocalStorage();
  }

  function replace(...items: Array<T>) {
    _array.value = items;
    saveToLocalStorage();
  }

  function remove(item: T) {
    const index = _array.value.findIndex((existingItem) =>
      isEqual ? isEqual(existingItem, item) : existingItem === item
    );
    if (index !== -1) _array.value.splice(index, 1);
    saveToLocalStorage();
  }

  return {
    arrayRecentsLast,
    arrayRecentsFirst,
    push,
    remove,
    replace,
  };
}

export function safeParseJSON<T = unknown>(
  json: string,
  defaultValueOnError: T | null = null
): T | null {
  try {
    return JSON.parse(json) as T;
  } catch (e) {
    return defaultValueOnError;
  }
}

export function numberStringToTwoDecimalPlaces(val: string): number {
  return Number(parseFloat(val).toFixed(2));
}

interface WindowWithGleap extends Window {
  Gleap?: {
    startFeedbackFlow: (flow: string) => void;
  };
}

export function openContactUsForm() {
  const windowWithGleap = window as WindowWithGleap;
  if (windowWithGleap.Gleap) {
    // https://app.gleap.io/projects/624dcd6d00ec5a00373568d0/settings/feedbackactions
    // "Enterprise Subscription Inquiry" feedback flow in Gleap
    windowWithGleap.Gleap.startFeedbackFlow(GleapFlows.INQUIRY);
  } else {
    const root = useRoot();
    // fallback in case gleap is not set up
    root.$dialog({
      title: 'Enterprise Subscription Inquiry',
      html: true,
      message: `
        <p>
          Please reach out to us at <a class="primary-link" href="mailto:sales@devstride.com">sales@devstride.com</a> to discuss your enterprise subscription needs.
        </p>
      `,
      okLabel: 'Close',
      hideCancelButton: true,
      color: Colors.BLUE,
    });
  }
}

export const deterministicStringify = configure({ deterministic: true });

export function insecureHash(str: string) {
  let hash = 0;
  if (str.length === 0) {
    return hash.toString(16);
  }
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return hash.toString(16);
}

/**
 * Escapes spaces and other characters in valid URLs without double encoding issues that `encodeURI` suffers from.
 */
export function escapeURL(url: string) {
  try {
    return new URL(url).href;
  } catch (e) {
    return url;
  }
}

export function isItemNavigable(item: PreparedItem) {
  return (
    item.itemType === ItemType.FOLDER || item.workType?.children.length > 0
  );
}

export function getTimeSpentSecondsBasedOnItemType(item: PreparedWorkItem) {
  const isLeafItem = !item.workType.children.length;
  if (!isLeafItem) {
    if (!item.aggregations) return 0;
    return Object.values(item.aggregations).reduce(
      (acc, value) => acc + value.totalTimeSpentSeconds,
      0
    );
  }

  // If time is tracked at subtask level, sum up subtask time
  if (item.isSubItemTimeSpent && item.subItems?.length) {
    return item.subItems.reduce(
      (acc, subItem) => acc + (subItem.timeSpentSeconds || 0),
      0
    );
  }

  return item.timeSpentSeconds;
}

export function isNumStringGreaterThanZero(numStr: string) {
  const number = Number(numStr.replace(/,/g, ''));
  return number > 0;
}

export function roundAccurately(number: number, decimalPlaces = 1): number {
  // This will round the number to the specified decimal places accurately.
  const roundedNumber = Number(
    Math.round(Number(number + 'e' + decimalPlaces)) + 'e-' + decimalPlaces
  );
  // Convert the rounded number to a fixed string and then to a float which removes trailing zeros.
  return parseFloat(roundedNumber.toFixed(decimalPlaces));
}

export const isLocalDev = import.meta.env.MODE === 'development';
export const isHostedDev = window.location.hostname.endsWith('.dev');
export const isDev = isLocalDev || isHostedDev;

export function escapeHtml(input: string): string {
  return input
    .replace(/&/g, '&amp;') // Must be done first!
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

export function useStorageWithComputedKey<T>(
  key: ComputedRef<string>,
  defaults: T
) {
  let storage = useStorage<T>(key.value, defaults);
  const storageReactivityCounter = ref(0);

  const ret = computed<T>({
    get: () => {
      storageReactivityCounter.value; // Ensure the computed is reactive to the storage changes
      return storage.value;
    },
    set: (value: T) => {
      storage.value = value;
    },
  });

  watch(key, () => {
    storage = useStorage<T>(key.value, defaults);
    storageReactivityCounter.value++;
  });

  return ret;
}

/**
 * Browser based dblclick is flaky, this helper implements a more reliable dblclick using click events.
 * To use, hook the returned handler into the click event.
 */
export function useDblClick<T extends unknown[]>(
  cb: (e: MouseEvent, ...args: T) => void,
  maxDelayBetweenClicks = 300
) {
  let dblClickState: {
    mouseEvent: MouseEvent;
    time: Date;
  } | null = null;

  async function handler(event: MouseEvent, ...args: T) {
    let wasDblClick = false;

    if (!dblClickState) {
      // handle first click
      dblClickState = {
        mouseEvent: event,
        time: new Date(),
      };
      await timeout(maxDelayBetweenClicks);
      dblClickState = null;
    } else {
      // handle second click
      const { mouseEvent, time } = dblClickState;
      const isSameClick =
        mouseEvent.clientX === event.clientX &&
        mouseEvent.clientY === event.clientY;
      const isSameTime = Date.now() - time.getTime() < maxDelayBetweenClicks;
      if (isSameClick && isSameTime) {
        wasDblClick = true;
        dblClickState = null;
      }
    }

    // event handling
    if (wasDblClick) {
      cb(event, ...args);
    }
  }

  return {
    handler,
  };
}

/**
 * Converts a number to a string with 'px' appended to it.
 */
export const toPx = (val: number) => `${val}px`;

/** reactive element size using a ref and resize observer */
export function useElementSize(
  el: Ref<HTMLElement[] | HTMLElement | null | undefined>
) {
  const width = ref(0);
  const height = ref(0);
  const reactivityCounter = ref(0);

  const observer = new ResizeObserver((entries) => {
    if (!el.value) return;
    const entry = entries[0];
    width.value = entry.contentRect.width;
    height.value = entry.contentRect.height;
    ++reactivityCounter.value;
  });

  watch(
    el,
    (newEl, oldEl) => {
      if (oldEl) {
        if (Array.isArray(oldEl)) {
          if (oldEl[0]) observer.unobserve(oldEl[0]);
        } else observer.unobserve(oldEl);
      }
      if (newEl) {
        if (Array.isArray(newEl)) {
          if (newEl[0]) observer.observe(newEl[0]);
        } else observer.observe(newEl);
      }
    },
    { immediate: true }
  );

  const stop = () => {
    if (!el.value) return;
    if (Array.isArray(el.value)) {
      if (el.value[0]) observer.unobserve(el.value[0]);
    } else observer.unobserve(el.value);
  };

  onUnmounted(() => {
    stop();
  });

  return { width, height, reactivityCounter, stop };
}

export function getBoundingClientRectWithoutReflow(
  el: HTMLElement
): Promise<DOMRect> {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const rect = entry.boundingClientRect;
      observer.disconnect();
      resolve(rect);
    });

    observer.observe(el);
  });
}

export function getScrollParent(
  node: HTMLElement | null | undefined
): HTMLElement | null {
  if (!node) return null;
  const overflow = getComputedStyle(node).overflow;
  const isScrollable = overflow.match(/(auto|scroll)/);
  if (isScrollable) return node;
  return getScrollParent(node.parentElement);
}

export function isHtmlElementEmpty(html: string): boolean {
  const element = document.createElement('div');
  element.innerHTML = html;
  const hasNoText = element.textContent?.trim() === '';
  const mediaTags = element.querySelectorAll(
    'img, video, audio, iframe, object, embed'
  );
  return hasNoText && mediaTags.length === 0;
}

export function validateRelationshipDates(
  currentItemDueDate:
    | string
    | null
    | undefined /** localDueDate that has not been set yet */,
  currentItemStartDate:
    | string
    | null
    | undefined /** localStartDate that has not been set yet */,
  relatedItems: Array<PreparedRelationship> | undefined,
  currentItem: PreparedItem | undefined
): string {
  if (!currentItem || !relatedItems) return '';

  const { dateTimeFormat } = useIntl();
  const currentDueDate = currentItemDueDate ? dayjs(currentItemDueDate) : null;
  const currentStartDate = currentItemStartDate
    ? dayjs(currentItemStartDate)
    : null;
  const blockedByDueDateItems: PreparedRelationship[] = [];
  const blockedByStartDateItems: PreparedRelationship[] = [];
  const blocksDueDateItems: PreparedRelationship[] = [];
  const blocksStartDateItems: PreparedRelationship[] = [];

  relatedItems.forEach((item) => {
    const relation = item.type;
    const relatedDue = item.contextItem.dueDate
      ? dayjs(item.contextItem.dueDate)
      : null;
    const relatedStart = item.contextItem.startDate
      ? dayjs(item.contextItem.startDate)
      : null;

    // Dependencies (parent) case
    if (relation === RelationshipType.BLOCKED_BY) {
      // check start date validations
      // relatedStart with currentStartDate
      if (
        currentStartDate &&
        relatedStart?.isValid() &&
        relatedStart.isAfter(currentStartDate)
      ) {
        blockedByStartDateItems.push(item);
      }
      // check due date validations
      // relatedDue with currentStartDate
      else if (
        currentStartDate &&
        relatedDue?.isValid() &&
        relatedDue.isAfter(currentStartDate)
      ) {
        blockedByDueDateItems.push(item);
      }
      // relatedDue with currentDueDate
      else if (
        currentDueDate &&
        relatedDue?.isValid() &&
        relatedDue.isAfter(currentDueDate)
      ) {
        blockedByDueDateItems.push(item);
      }
      // relatedDue with currentStartDate
      else if (
        currentDueDate &&
        relatedStart?.isValid() &&
        relatedStart.isAfter(currentDueDate)
      ) {
        blockedByDueDateItems.push(item);
      }
    }
    // Dependents (child) case
    else if (relation === RelationshipType.BLOCKS) {
      // check due date validations
      // relatedDue with currentDueDate
      if (
        currentDueDate &&
        relatedDue?.isValid() &&
        relatedDue.isBefore(currentDueDate)
      ) {
        blocksDueDateItems.push(item);
      }
      // relatedDue with currentStartDate
      else if (
        currentDueDate &&
        relatedStart?.isValid() &&
        relatedStart.isBefore(currentDueDate)
      ) {
        blocksDueDateItems.push(item);
      }
      // check start date validations
      // relatedStart with currentDueDate
      else if (
        currentStartDate &&
        relatedStart?.isValid() &&
        relatedStart.isBefore(currentStartDate)
      ) {
        blocksStartDateItems.push(item);
      }
      // relatedStart with currentStartDate
      else if (
        currentStartDate &&
        relatedDue?.isValid() &&
        relatedDue.isBefore(currentStartDate)
      ) {
        blocksStartDateItems.push(item);
      }
    }
  });
  let message = '';

  if (blockedByDueDateItems.length > 0 && currentDueDate) {
    const blockedByItemsString = blockedByDueDateItems
      .map((item) => `<strong>${item.referenceId}</strong>`)
      .join(', ');

    message += `<p>The selected due date <strong>${currentDueDate.format(
      dateTimeFormat.value.date
    )}</strong>
    violates the dependency chain date constraints. <br> The due date should be after the following dependencies start and due date(s): ${blockedByItemsString}.</p>
    `;
  } else if (blockedByStartDateItems.length > 0 && currentStartDate) {
    const blockedByItemsString = blockedByStartDateItems
      .map((item) => `<strong>${item.referenceId}</strong>`)
      .join(', ');

    message += `<p>The selected start date <strong>${currentStartDate.format(
      dateTimeFormat.value.date
    )}</strong> violates the dependency chain date constraints. <br> The start date should be after the following dependencies start and due date(s): ${blockedByItemsString}.</p>
    `;
  } else if (blocksStartDateItems.length > 0 && currentStartDate) {
    const blocksItemsString = blocksStartDateItems
      .map((item) => `<strong>${item.referenceId}</strong>`)
      .join(', ');

    message += `<p>The selected start date <strong>${currentStartDate.format(
      dateTimeFormat.value.date
    )}</strong> violates the dependency chain date constraints. <br> The start date should be before the following dependents start and due date(s): ${blocksItemsString}.</p>
    `;
  } else if (blocksDueDateItems.length > 0 && currentDueDate) {
    const blocksItemsString = blocksDueDateItems
      .map((item) => `<strong>${item.referenceId}</strong>`)
      .join(', ');

    message += `<p>The selected due date <strong>${currentDueDate.format(
      dateTimeFormat.value.date
    )}</strong> violates the dependency chain date constraints. <br> The due date should be before the following dependents start and due date(s): ${blocksItemsString}.</p>
    `;
  }

  if (message) {
    message += `<p>Are you sure you still want to proceed?</p>`;
  }

  return message;
}

const currentItemRelationships = ref<PreparedRelationship[]>();

async function fetchCurrentItemRelationships(item: PreparedItem) {
  if (!item || !item.relationships) return [];
  const itemNumbers = item.relationships.map((r) => r.referenceId);
  const itemRelationships = item.relationships;
  if (!itemNumbers.length) return [];
  try {
    const response = await fetchItemListByNumbers({ numbers: itemNumbers });
    if (!response.length || !itemRelationships) {
      return (currentItemRelationships.value = []);
    }
    currentItemRelationships.value = response.map((relatedItem) => {
      const relationship = itemRelationships.find(
        (r) => r.referenceId === relatedItem.number
      )!;
      return {
        ...relationship,
        key: relationship.referenceId,
        contextItem: relatedItem,
        referenceItem: relatedItem,
      };
    });
  } catch (e: unknown) {
    console.error(e);
  }
}

export const handleItemToRelationshipsDateValidation = async ({
  item,
  localItemDueDate,
  localItemStartDate,
}: {
  item: PreparedItem;
  localItemDueDate: string | null;
  localItemStartDate?: string | null;
}): Promise<boolean> => {
  if (!item) return true;
  await fetchCurrentItemRelationships(item);
  const root = useRoot();

  const organization = root.$store.organization.activeOrganization;
  if (organization?.enableStaticMode) return true;

  const validationMessage = validateRelationshipDates(
    localItemDueDate,
    localItemStartDate,
    currentItemRelationships.value,
    item
  );
  if (!validationMessage) return true;
  const shouldUpdate = new Promise<boolean>((resolve) => {
    root.$dialog({
      title: 'Relationships Date Warning',
      color: Colors.YELLOW,
      message: validationMessage,
      html: true,
      okLabel: 'Confirm',
      cancelLabel: 'Cancel',
      persistent: true,
      onOk: async () => resolve(true),
      onCancel: async () => resolve(false),
    });
  });
  if (!(await shouldUpdate)) return false;
  return true;
};

// Validate Item Due Dates with related items Due dates End
